/**
 * One answer to "is this app in dark mode".
 *
 * There were two, and they disagreed. BAMFull writes the flag with
 * String(boolean), so localStorage holds "true" or "false"; the shell in
 * dashboard/index.js read it as `=== "1"`. Every coach in dark mode -
 * which is nearly all of them, and the default - therefore had
 * `dark = false` outside BAMFull, so the announcement banner rendered
 * with the LIGHT palette over the dark app: #111111 text on a dark
 * background, effectively unreadable.
 *
 * Nothing caught it because the banner is the only thing mounted outside
 * BAMFull that needs the theme, and it is only on screen when there is
 * an active announcement.
 *
 * So the read lives here, once, and the two callers share it.
 */
import { isNative } from "../../utils/platform";

export const DARK_MODE_KEY = "bam_dark_mode";
// Dispatched by BAMFull when a coach toggles the theme, so anything
// mounted outside its tree can follow. A storage event would not do it:
// the browser fires that in OTHER tabs, never the one that wrote.
export const DARK_MODE_EVENT = "bam:darkmode";

/**
 * The stored preference, or the best guess when there isn't one.
 * Mirrors BAMFull's initialiser exactly; change both together, or
 * better, change only this.
 */
export function resolveDarkMode() {
  try {
    const saved = localStorage.getItem(DARK_MODE_KEY);
    if (saved !== null) return saved === "true";
  } catch { /* private mode, blocked storage */ }
  // The native app boots dark whatever the OS says: the brand is
  // dark-first and the splash is tuned for it, so a light dashboard
  // straight after a black launch screen looks broken.
  if (isNative()) return true;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme:dark)").matches;
  }
  return true;
}

/** Tell the rest of the app the theme changed. */
export function announceDarkMode(next) {
  try { window.dispatchEvent(new CustomEvent(DARK_MODE_EVENT, { detail: { dark: next } })); } catch {}
}
